import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { Spinner, Text } from "@ui-kitten/components";
import Button from "components/Button";
import Dialog from "components/Dialog";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";
import { EventDetails, TicketDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { BarcodeScreenProps } from "navigation/types";
import {
  fetchTicketBarcodeByIdsFetcher,
  scanTicketsRequest,
} from "queries/ticket.queries";
import React, { useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import toast from "utils/toast";
import BarcodeCarousel from "./BarcodeCarousel";
import TicketBackModal from "./TicketBackModal";

const Barcode: React.FC<BarcodeScreenProps> = ({ route, navigation }) => {
  const { account, accountEvents: events, fetchAccount } = useAccount();
  const { groupCode } = route.params;

  const [event, setEvent] = useState<EventDetails | null>(null);
  const [tickets, setTickets] = useState<TicketDetails[]>([]);

  const { mutateAsync: fetchTicketBarcodeByIds } = useMutation(
    fetchTicketBarcodeByIdsFetcher
  );
  const { mutateAsync: scanTickets, isLoading } =
    useMutation(scanTicketsRequest);

  if (!account) return null;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [ticketsWithBarcode, setTicketsWithBarcode] = useState<TicketDetails[]>(
    []
  );
  const [visible, setVisible] = useState(false);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

  const accessoryRight = () => (
    <Pressable onPress={() => setVisible(true)}>
      <MaterialCommunityIcons
        name="dots-horizontal-circle"
        size={24}
        color="white"
      />
    </Pressable>
  );

  const scanableTickets = tickets.filter(
    (ticket) => ticket.scanlessEntry && ticket.status !== "SCANNED"
  );

  const handleScanTickets = async () => {
    await scanTickets(scanableTickets.map((t) => t.id));
    await fetchAccount();
    toast.success("Tickets scanned successfully");
    setConfirmDialogVisible(false);
  };

  useEffect(() => {
    if (!account || !events) {
      return;
    }
    const eventDetail = events.find(
      (event) => event.groupCode.groupCode === groupCode
    );
    if (eventDetail) {
      setEvent(eventDetail.event);
      setTickets(eventDetail.tickets);
    } else {
      setTimeout(() => {
        navigation.navigate("Events");
      }, 100);
    }
  }, [account, events]);

  useEffect(() => {
    if (tickets.length === 0) return;
    fetchTicketBarcodeByIds(tickets)
      .then((res) => setTicketsWithBarcode(res as TicketDetails[]))
      .catch(() => navigation.navigate("EventDetail", { groupCode }));
  }, [tickets]);

  if (!event) return null;

  return (
    <MainContainer
      goBackAction={() => navigation.replace("EventDetail", { groupCode })}
      accessoryRight={accessoryRight}
    >
      <TicketBackModal
        visible={visible}
        setVisible={setVisible}
        tickets={ticketsWithBarcode}
        currentIndex={currentIndex}
        event={event}
      />
      <View
        style={{
          flex: 1,
          overflow: "hidden",
          marginTop: 25,
        }}
      >
        <View style={{ flexGrow: 1 }}>
          {scanableTickets.length > 0 && (
            <>
              <Button
                style={{ alignSelf: "center", marginBottom: 15 }}
                onPress={() => setConfirmDialogVisible(true)}
              >
                {`Use All ${scanableTickets.length} Ticket(s)`}
              </Button>
              <Dialog
                visible={confirmDialogVisible}
                setVisible={setConfirmDialogVisible}
                cancelText="Cancel"
                okText="CONFIRM USE TICKET(S)"
                onOk={handleScanTickets}
                loading={isLoading}
              >
                <Text category="h6">
                  Are you sure you want to use all tickets?
                </Text>
                <Spacer />
                <Text>
                  This action should only be performed by arena staff at the
                  time of entry.
                </Text>
              </Dialog>
            </>
          )}

          {ticketsWithBarcode.length === 0 ? (
            <View style={{ flex: 1 }}>
              <Spinner size="giant" status="primary"></Spinner>
            </View>
          ) : (
            <BarcodeCarousel
              tickets={ticketsWithBarcode}
              setCurrentIndex={setCurrentIndex}
              currentIndex={currentIndex}
            />
          )}
          <Text style={{ textAlign: "center", marginTop: 15 }}>
            {currentIndex + 1} / {tickets.length}
          </Text>
        </View>

      </View>
    </MainContainer>
  );
};

export default Barcode;
